import React, { useEffect, useRef, useState } from "react";
import DeviceBadge from "./DeviceBadge";
import StatusBadge from "./StatusBadge";

const DEFAULT_ICE_SERVERS = [
    { urls: "stun:stun.cloudflare.com:3478" },
];

function normalizeIceServer(server) {
    if (typeof server === "string" && server.trim()) {
        return { urls: server.trim() };
    }
    if (!server || typeof server !== "object") return null;
    const urls = server.urls || server.url;
    if (!urls) return null;

    const normalized = { urls };
    if (typeof server.username === "string") {
        normalized.username = server.username;
    }
    if (typeof server.credential === "string") {
        normalized.credential = server.credential;
    }
    if (typeof server.credentialType === "string") {
        normalized.credentialType = server.credentialType;
    }
    return normalized;
}

function parseIceServersEnv(rawValue) {
    if (!rawValue || !rawValue.trim()) return null;
    const raw = rawValue.trim();

    try {
        const parsed = JSON.parse(raw);
        const list = Array.isArray(parsed) ? parsed : [parsed];
        const normalized = list.map(normalizeIceServer).filter(Boolean);
        if (normalized.length > 0) return normalized;
    } catch (err) {
        // Fallback to comma-separated STUN/TURN URLs for easier ops config.
        const normalized = raw
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean)
            .map((url) => ({ urls: url }));
        if (normalized.length > 0) return normalized;
    }
    return null;
}

const ICE_SERVERS =
    parseIceServersEnv(process.env.REACT_APP_WEBRTC_ICE_SERVERS) ||
    DEFAULT_ICE_SERVERS;
const STREAM_TRANSPORT = (
    process.env.REACT_APP_STREAM_TRANSPORT || "auto"
).toLowerCase();

function toIframeStreamPageUrl(rawUrl) {
    if (typeof rawUrl !== "string") return "";
    const value = rawUrl.trim();
    if (!value) return "";

    const match = value.match(/^([^?#]*)(.*)$/);
    if (!match) return value;

    const pathPart = match[1];
    const suffix = match[2] || "";
    if (/\.[a-z0-9]+$/i.test(pathPart)) return value;

    const normalizedPath = pathPart.endsWith("/") ? pathPart : `${pathPart}/`;
    return `${normalizedPath}${suffix}`;
}

function normalizeStreamUrl(rawUrl) {
    if (typeof rawUrl !== "string") return "";
    return rawUrl.trim().replace(/\/+$/, "");
}

function buildStreamCandidates(rawUrl) {
    const baseUrl = normalizeStreamUrl(rawUrl);
    if (!baseUrl) return [];
    return [
        baseUrl,
        `${baseUrl}/playlist.m3u8`,
        `${baseUrl}/index.m3u8`,
        `${baseUrl}.m3u8`,
    ];
}

export const VideoPlayer = ({
    deviceBaseUrl,
    deviceName,
    playUrl,
    playing,
    onPlay,
    onStop,
    selectedSubDeviceLabel,
    selectedSubDeviceUrl,
    selectedDevice,
    selectedSubDevice,
}) => {
    const videoOriginalRef = useRef(null);
    const videoProcessedRef = useRef(null);
    const pcsRef = useRef({ original: null, processed: null });
    const hlsPlayersRef = useRef({ original: null, processed: null });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [userInitiatedPlay, setUserInitiatedPlay] = useState(false);
    const [thumbSrcIndex, setThumbSrcIndex] = useState(0);
    const [renderMode, setRenderMode] = useState({
        original: "video",
        processed: "video",
    });
    const [iframeSrc, setIframeSrc] = useState({
        original: "",
        processed: "",
    });
    const isQuadLayout = selectedDevice?.layout === "quad";
    const quadStreams = React.useMemo(() => {
        if (!isQuadLayout || !Array.isArray(selectedDevice?.subDevices)) {
            return [];
        }
        return selectedDevice.subDevices
            .filter((stream) => stream?.url)
            .slice(0, 4)
            .map((stream, index) => ({
                id: stream.id || stream.url || `stream-${index + 1}`,
                label: stream.label || stream.id || `Stream ${index + 1}`,
                url: normalizeStreamUrl(stream.url),
            }));
    }, [isQuadLayout, selectedDevice]);

    // 1) Check if the selected device/subdevice has a 'processed' field set to true
    // This comes directly from the devices.json or REACT_APP_DEVICE_SOURCES
    const hasProcessed = React.useMemo(() => {
        // Check if selectedSubDevice has processed field
        if (
            selectedSubDevice &&
            typeof selectedSubDevice.processed === "boolean"
        ) {
            return selectedSubDevice.processed;
        }
        // Fallback to checking the main device
        if (selectedDevice && typeof selectedDevice.processed === "boolean") {
            return selectedDevice.processed;
        }
        // Default to false if not specified
        return false;
    }, [selectedDevice, selectedSubDevice]);

    const { originalStreamUrl, processedStreamUrl } = React.useMemo(() => {
        const activeUrl = normalizeStreamUrl(playUrl || selectedSubDeviceUrl || "");
        if (!activeUrl) {
            return { originalStreamUrl: "", processedStreamUrl: "" };
        }

        const isProcessedSelection =
            Boolean(selectedSubDevice?.processed) ||
            /\/processed$/i.test(activeUrl);

        if (isProcessedSelection) {
            return {
                originalStreamUrl: activeUrl.replace(/\/processed$/i, ""),
                processedStreamUrl: activeUrl,
            };
        }

        return {
            originalStreamUrl: activeUrl,
            processedStreamUrl: `${activeUrl}/processed`,
        };
    }, [playUrl, selectedSubDeviceUrl, selectedSubDevice]);

    // 2) Start/stop streams and handle WebRTC/HLS fallback. Separated to avoid unreachable code
    // inside useEffect (cleanup return must be last statement in the effect callback).
    useEffect(() => {
        // Start streams only when a playUrl is provided AND playing === true
        const NEGOTIATION_TIMEOUT = 20000; // ms (public networks can take longer to gather ICE candidates)
        const peerConnections = pcsRef.current;
        const hlsPlayers = hlsPlayersRef.current;

        if (isQuadLayout || !originalStreamUrl || !playing) {
            // stop/cleanup if any existing PCs
            if (peerConnections.original) {
                try {
                    peerConnections.original.close();
                } catch { }
                peerConnections.original = null;
            }
            if (peerConnections.processed) {
                try {
                    peerConnections.processed.close();
                } catch { }
                peerConnections.processed = null;
            }
            // stop/cleanup hls.js players
            if (hlsPlayers.original) {
                try {
                    hlsPlayers.original.destroy();
                } catch { }
                hlsPlayers.original = null;
            }
            if (hlsPlayers.processed) {
                try {
                    hlsPlayers.processed.destroy();
                } catch { }
                hlsPlayers.processed = null;
            }
            // clear video elements
            if (videoOriginalRef.current) {
                try {
                    videoOriginalRef.current.srcObject = null;
                } catch { }
                try {
                    videoOriginalRef.current.src = "";
                    videoOriginalRef.current.load();
                } catch { }
            }
            if (videoProcessedRef.current) {
                try {
                    videoProcessedRef.current.srcObject = null;
                } catch { }
                try {
                    videoProcessedRef.current.src = "";
                    videoProcessedRef.current.load();
                } catch { }
            }
            setRenderMode({ original: "video", processed: "video" });
            setIframeSrc({ original: "", processed: "" });
            setIsLoading(false);
            return;
        }

        let mounted = true;
        setIsLoading(true);
        setError(null);
        setRenderMode({ original: "video", processed: "video" });
        setIframeSrc({ original: "", processed: "" });

        async function playStream(videoRef, targetStreamUrl) {
            const whpEndpointBase = normalizeStreamUrl(targetStreamUrl);
            const pc = new RTCPeerConnection({
                iceServers: ICE_SERVERS,
            });
            pc.addTransceiver("video", { direction: "recvonly" });
            pc.addTransceiver("audio", { direction: "recvonly" });

            pc.ontrack = (event) => {
                if (!mounted) return;
                if (event.streams && event.streams[0]) {
                    if (!videoRef.current) return;

                    try {
                        videoRef.current.srcObject = event.streams[0];
                    } catch (e) {
                        console.warn("Failed to assign srcObject on track", e);
                    }
                    setIsLoading(false);
                    // Ya no hace falta llamar a .play() aquí porque la etiqueta ya tiene autoPlay
                }
            };

            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);

            // Wait for ICE gathering, but don't hang forever — use a timeout
            await Promise.race([
                new Promise((resolve) => {
                    if (pc.iceGatheringState === "complete") return resolve();
                    const check = () => {
                        if (pc.iceGatheringState === "complete") {
                            pc.removeEventListener(
                                "icegatheringstatechange",
                                check,
                            );
                            resolve();
                        }
                    };
                    pc.addEventListener("icegatheringstatechange", check);
                }),
                new Promise((resolve) =>
                    setTimeout(resolve, NEGOTIATION_TIMEOUT),
                ),
            ]);

            // POST local SDP to the WHEP endpoint and set remote description.
            // Servers sometimes expose /whep at the stream path or at the origin root.
            const attempted = [];
            if (whpEndpointBase) attempted.push(`${whpEndpointBase}/whep`);
            try {
                const baseOrigin =
                    typeof window !== "undefined" && window.location
                        ? window.location.origin
                        : undefined;
                const u = baseOrigin
                    ? new URL(targetStreamUrl, baseOrigin)
                    : new URL(targetStreamUrl);
                // For /api/streams routes, keep WHEP under the same route
                // and avoid falling back to origin /whep (can hit wrong upstream).
                if (!u.pathname.startsWith("/api/streams")) {
                    attempted.push(`${u.origin}/whep`);
                }
            } catch (e) { }

            let answerSdp = null;
            for (const url of attempted) {
                try {
                    const res = await fetch(url, {
                        method: "POST",
                        headers: { "Content-Type": "application/sdp" },
                        body: pc.localDescription.sdp,
                    });
                    if (res.ok) {
                        answerSdp = await res.text();
                        await pc.setRemoteDescription({
                            type: "answer",
                            sdp: answerSdp,
                        });
                        break;
                    } else {
                        console.warn(
                            "WHEP POST returned",
                            res.status,
                            "for",
                            url,
                        );
                    }
                } catch (err) {
                    console.warn("WHEP POST failed for", url, err);
                }
            }
            if (!answerSdp)
                throw new Error(
                    "WHEP POST failed (no candidate endpoints succeeded)",
                );

            return pc;
        }

        (async () => {
            function applyIframeFallback(playerKey, rawUrl) {
                if (!mounted) return false;
                const iframeUrl = toIframeStreamPageUrl(rawUrl);
                if (!iframeUrl) return false;

                if (peerConnections[playerKey]) {
                    try {
                        peerConnections[playerKey].close();
                    } catch { }
                    peerConnections[playerKey] = null;
                }
                if (hlsPlayers[playerKey]) {
                    try {
                        hlsPlayers[playerKey].destroy();
                    } catch { }
                    hlsPlayers[playerKey] = null;
                }

                const targetVideoRef =
                    playerKey === "processed"
                        ? videoProcessedRef
                        : videoOriginalRef;
                if (targetVideoRef.current) {
                    try {
                        targetVideoRef.current.srcObject = null;
                    } catch { }
                    try {
                        targetVideoRef.current.src = "";
                        targetVideoRef.current.load();
                    } catch { }
                }

                setRenderMode((prev) => ({ ...prev, [playerKey]: "iframe" }));
                setIframeSrc((prev) => ({ ...prev, [playerKey]: iframeUrl }));
                console.info(
                    `Using iframe fallback for ${playerKey} stream:`,
                    iframeUrl,
                );
                return true;
            }

            try {
                // Optional compile-time switch to force HLS or iframe and bypass WebRTC negotiation.
                if (STREAM_TRANSPORT === "hls") {
                    throw new Error("__HLS_ONLY_MODE__");
                }
                if (STREAM_TRANSPORT === "iframe") {
                    throw new Error("__IFRAME_ONLY_MODE__");
                }

                peerConnections.original = await playStream(
                    videoOriginalRef,
                    originalStreamUrl,
                );
                // Processed stream is optional. Keep original stream alive even if processed fails.
                if (hasProcessed !== false) {
                    try {
                        peerConnections.processed = await playStream(
                            videoProcessedRef,
                            processedStreamUrl,
                        );
                    } catch (processedErr) {
                        console.warn(
                            "Processed stream negotiation failed, keeping original stream",
                            processedErr,
                        );
                        if (peerConnections.processed) {
                            try {
                                peerConnections.processed.close();
                            } catch { }
                            peerConnections.processed = null;
                        }
                    }
                }
            } catch (err) {
                const isHlsOnlyMode =
                    err && typeof err.message === "string"
                        ? err.message === "__HLS_ONLY_MODE__"
                        : false;
                const isIframeOnlyMode =
                    err && typeof err.message === "string"
                        ? err.message === "__IFRAME_ONLY_MODE__"
                        : false;
                if (!isHlsOnlyMode && !isIframeOnlyMode) {
                    console.warn(
                        "Stream negotiation failed or timed out, attempting HTML5 fallback",
                        err,
                    );
                }
                // Try HLS (m3u8) fallback first, then naive HTML5 src fallback.
                async function tryHlsFallback(videoRef, candidates) {
                    if (!videoRef.current) return false;
                    const playerKey =
                        videoRef === videoOriginalRef
                            ? "original"
                            : "processed";
                    // If browser supports native HLS (Safari), try assigning src directly
                    const canNativeHls =
                        videoRef.current.canPlayType &&
                        videoRef.current.canPlayType(
                            "application/vnd.apple.mpegurl",
                        );
                    if (canNativeHls) {
                        for (let c of candidates) {
                            // preflight: fetch the URL to check content-type; if it's HTML, try to extract an m3u8 link from the page
                            try {
                                const pre = await fetch(c, { method: "GET" });
                                const ct =
                                    pre.headers.get("content-type") || "";
                                if (ct.includes("html")) {
                                    try {
                                        const text = await pre.text();
                                        const re =
                                            /(["'()])((https?:)?\/\/[^"'()]+?\.m3u8)(["'()])/g;
                                        const reRel =
                                            /(["'()])([^"'()]+?\.m3u8)(["'()])/g;
                                        let match = re.exec(text);
                                        let found = null;
                                        if (match && match[2]) found = match[2];
                                        else {
                                            match = reRel.exec(text);
                                            if (match && match[2]) {
                                                try {
                                                    found = new URL(match[2], c)
                                                        .href;
                                                } catch (e) {
                                                    found = null;
                                                }
                                            }
                                        }
                                        if (found) {
                                            console.info(
                                                "Extracted m3u8 from HTML page:",
                                                found,
                                            );
                                            c = found; // replace candidate with the extracted playlist
                                        }
                                    } catch (e) {
                                        console.warn(
                                            "Failed to parse HTML for m3u8",
                                            e,
                                        );
                                    }
                                }
                            } catch (e) {
                                // network error, continue to try setting src (it will likely fail)
                                console.warn(
                                    "Preflight fetch failed for HLS candidate",
                                    c,
                                    e,
                                );
                            }

                            try {
                                videoRef.current.srcObject = null;
                                videoRef.current.src = c;
                                videoRef.current.playsInline = true;
                                console.info("Trying native HLS src", c);
                                if (userInitiatedPlay) {
                                    const p = videoRef.current.play();
                                    if (p && p.catch) p.catch(() => { });
                                }
                                return true;
                            } catch (e) {
                                console.warn(
                                    "Native HLS attempt failed for",
                                    c,
                                    e,
                                );
                            }
                        }
                        return false;
                    }

                    // Try using hls.js dynamically to avoid adding runtime cost if not needed
                    try {
                        const HlsModule = await import("hls.js");
                        const Hls = HlsModule.default || HlsModule;
                        if (!Hls.isSupported()) return false;

                        for (let c of candidates) {
                            // preflight: check if this candidate returns HTML that contains a playlist link
                            try {
                                const pre = await fetch(c, { method: "GET" });
                                const ct =
                                    pre.headers.get("content-type") || "";
                                if (ct.includes("html")) {
                                    try {
                                        const text = await pre.text();
                                        const re =
                                            /(["'()])((https?:)?\/\/[^"'()]+?\.m3u8)(["'()])/g;
                                        const reRel =
                                            /(["'()])([^"'()]+?\.m3u8)(["'()])/g;
                                        let match = re.exec(text);
                                        let found = null;
                                        if (match && match[2]) found = match[2];
                                        else {
                                            match = reRel.exec(text);
                                            if (match && match[2]) {
                                                try {
                                                    found = new URL(match[2], c)
                                                        .href;
                                                } catch (e) {
                                                    found = null;
                                                }
                                            }
                                        }
                                        if (found) {
                                            console.info(
                                                "Extracted m3u8 from HTML page:",
                                                found,
                                            );
                                            c = found;
                                        }
                                    } catch (e) {
                                        console.warn(
                                            "Failed to parse HTML for m3u8",
                                            e,
                                        );
                                    }
                                }
                            } catch (e) {
                                console.warn(
                                    "Preflight fetch failed for HLS candidate",
                                    c,
                                    e,
                                );
                            }

                            let hls = null;
                            try {
                                if (hlsPlayers[playerKey]) {
                                    try {
                                        hlsPlayers[playerKey].destroy();
                                    } catch { }
                                    hlsPlayers[playerKey] = null;
                                }
                                hls = new Hls();
                                hls.attachMedia(videoRef.current);
                                hls.loadSource(c);
                                // wait for manifest parsed or error
                                const ok = await new Promise((resolve) => {
                                    const onManifest = () => {
                                        cleanupListeners();
                                        resolve(true);
                                    };
                                    const onError = (event, data) => {
                                        if (data && data.fatal) {
                                            cleanupListeners();
                                            resolve(false);
                                        }
                                    };
                                    function cleanupListeners() {
                                        if (hls && Hls.Events) {
                                            hls.off(
                                                Hls.Events.MANIFEST_PARSED,
                                                onManifest,
                                            );
                                            hls.off(Hls.Events.ERROR, onError);
                                        }
                                    }
                                    hls.on(
                                        Hls.Events.MANIFEST_PARSED,
                                        onManifest,
                                    );
                                    hls.on(Hls.Events.ERROR, onError);
                                });
                                if (ok) {
                                    hlsPlayers[playerKey] = hls;
                                    console.info("HLS loaded", c);
                                    if (userInitiatedPlay) {
                                        const p = videoRef.current.play();
                                        if (p && p.catch) p.catch(() => { });
                                    }
                                    return true;
                                }
                                try {
                                    hls.destroy();
                                } catch { }
                            } catch (e) {
                                console.warn("hls.js failed for", c, e);
                                try {
                                    if (hls) hls.destroy();
                                } catch (e2) { }
                            }
                        }
                    } catch (e) {
                        console.warn("hls.js dynamic import failed", e);
                    }
                    return false;
                }

                try {
                    if (originalStreamUrl) {
                        const originalCandidates =
                            buildStreamCandidates(originalStreamUrl);
                        const processedCandidates =
                            buildStreamCandidates(processedStreamUrl);
                        let loadedOriginal = false;
                        let loadedProcessed = false;

                        if (!isIframeOnlyMode) {
                            // try HLS or native for original
                            if (videoOriginalRef.current) {
                                videoOriginalRef.current.srcObject = null;
                                loadedOriginal = await tryHlsFallback(
                                    videoOriginalRef,
                                    originalCandidates,
                                );
                            }

                            // try HLS or native for processed (guess path)
                            if (hasProcessed !== false && videoProcessedRef.current) {
                                videoProcessedRef.current.srcObject = null;
                                loadedProcessed = await tryHlsFallback(
                                    videoProcessedRef,
                                    processedCandidates,
                                );
                            }
                        }

                        // Last resort fallback: embed MediaMTX stream page in iframe.
                        if (!loadedOriginal) {
                            loadedOriginal = applyIframeFallback(
                                "original",
                                originalStreamUrl,
                            );
                        }

                        if (hasProcessed !== false && !loadedProcessed) {
                            loadedProcessed = applyIframeFallback(
                                "processed",
                                processedStreamUrl,
                            );
                        }

                        if (!loadedOriginal) {
                            throw new Error(
                                "No playback method succeeded for original stream",
                            );
                        }

                        setError(null);
                    }
                } catch (e2) {
                    console.error("Fallback failed", e2);
                    setError(e2.message || String(e2));
                }
                setIsLoading(false);
            }
        })();

        return () => {
            mounted = false;
            if (peerConnections.original) {
                try {
                    peerConnections.original.close();
                } catch { }
                peerConnections.original = null;
            }
            if (peerConnections.processed) {
                try {
                    peerConnections.processed.close();
                } catch { }
                peerConnections.processed = null;
            }
            if (hlsPlayers.original) {
                try {
                    hlsPlayers.original.destroy();
                } catch { }
                hlsPlayers.original = null;
            }
            if (hlsPlayers.processed) {
                try {
                    hlsPlayers.processed.destroy();
                } catch { }
                hlsPlayers.processed = null;
            }
        };
    }, [
        originalStreamUrl,
        processedStreamUrl,
        playing,
        userInitiatedPlay,
        hasProcessed,
        isQuadLayout,
    ]);
    // Reset thumbnail candidate index whenever selected subdevice changes
    useEffect(() => {
        setThumbSrcIndex(0);
    }, [selectedSubDeviceUrl]);

    const thumbBase = selectedSubDeviceUrl
        ? selectedSubDeviceUrl.replace(/\/+$/, "")
        : "";
    const thumbCandidates = thumbBase
        ? [
            `${thumbBase}/thumb.jpg`,
            `${thumbBase}/snapshot.jpg`,
            `${thumbBase}/preview.jpg`,
            `${thumbBase}/snapshot`,
            `${thumbBase}`,
        ]
        : [];
    const thumbSrc = thumbCandidates[thumbSrcIndex] || "";

    // When the user explicitly asked to play (clicked Play), attempt to start playback
    // for any video elements that already have a srcObject or src.
    useEffect(() => {
        if (!userInitiatedPlay) return;
        try {
            if (videoOriginalRef.current) {
                const v = videoOriginalRef.current;
                if ((v.srcObject || v.currentSrc || v.src) && v.play) {
                    const p = v.play();
                    if (p && p.catch) p.catch(() => { });
                }
            }
        } catch (e) { }
        try {
            if (videoProcessedRef.current) {
                const v2 = videoProcessedRef.current;
                if ((v2.srcObject || v2.currentSrc || v2.src) && v2.play) {
                    const p2 = v2.play();
                    if (p2 && p2.catch) p2.catch(() => { });
                }
            }
        } catch (e) { }
    }, [userInitiatedPlay]);

    const PLAYER_HEIGHT = "min(65vh, calc(100vh - 220px))";
    const originalIframeSrc =
        iframeSrc.original ||
        toIframeStreamPageUrl(originalStreamUrl || "");
    const processedIframeSrc =
        iframeSrc.processed ||
        toIframeStreamPageUrl(processedStreamUrl || "");

    const renderHeader = () => (
        <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
                {/* device badge */}
                <DeviceBadge playing={playing} />
                <div className="min-w-0">
                    <div className="text-[20px] font-extrabold text-foreground">
                        {deviceName || "No device"}
                    </div>
                    <div className="text-sm text-muted-foreground font-mono truncate">
                        {selectedSubDeviceLabel
                            ? `${selectedSubDeviceLabel} • ${isQuadLayout ? `${quadStreams.length} simultaneous streams` : selectedSubDeviceUrl}`
                            : deviceBaseUrl}
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-3">
                {/* status badge */}
                <div className="flex items-center gap-2">
                    <StatusBadge playing={playing} />
                </div>

                {/* play/stop control */}
                <div className="flex gap-2">
                    {playing ? (
                        <button
                            onClick={() => {
                                setUserInitiatedPlay(false);
                                onStop && onStop();
                            }}
                            className="px-4 py-2 rounded-lg border border-transparent font-bold shadow-md transition-colors"
                            style={{
                                background: "var(--color-alert)",
                                color: "var(--color-white)",
                            }}
                        >
                            Stop
                        </button>
                    ) : (
                        <button
                            onClick={() => {
                                setUserInitiatedPlay(true);
                                setError(null);
                                onPlay && onPlay();
                            }}
                            className="px-4 py-2 rounded-lg font-bold transition-colors shadow-md"
                            style={{
                                background: "var(--color-secondary)",
                                color: "var(--color-white)",
                            }}
                        >
                            Play
                        </button>
                    )}

                    {/* only Play/Stop controls remain */}
                </div>
            </div>
        </div>
    );

    if (!deviceBaseUrl) {
        return (
            <div className="flex-1 min-h-0 flex items-center justify-center bg-background">
                <div className="text-center text-muted-foreground">
                    <div className="text-[20px] font-semibold">
                        Select a device
                    </div>
                    <div className="mt-2 text-sm">
                        Choose a device on the left to view details
                    </div>
                </div>
            </div>
        );
    }

    if (isQuadLayout) {
        return (
            <div className="flex-1 min-h-0 h-full overflow-y-auto bg-background p-4 lg:p-6">
                <div
                    className={`card rounded-xl p-4 transition-shadow ${playing ? "shadow-2xl" : "shadow-lg"}`}
                >
                    {renderHeader()}
                </div>

                <div
                    className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 min-h-0"
                    style={{
                        height: "max(520px, calc(100% - 112px))",
                        gridAutoRows: "minmax(0, 1fr)",
                    }}
                >
                    {quadStreams.map((stream, index) => {
                        const frameSrc = toIframeStreamPageUrl(stream.url);
                        return (
                            <div
                                key={stream.id}
                                className="relative rounded-2xl overflow-hidden shadow-2xl border border-border"
                                style={{
                                    background: "var(--color-background)",
                                    minHeight: 0,
                                }}
                            >
                                <div
                                    className="absolute left-3 top-3 z-30 px-3 py-1 rounded-full backdrop-blur font-bold text-sm"
                                    style={{
                                        background: "rgba(0,0,0,0.45)",
                                        color: "var(--color-white)",
                                    }}
                                >
                                    {stream.label}
                                </div>
                                <div
                                    className="absolute right-3 top-3 z-30 px-2.5 py-1 rounded-md font-bold text-xs"
                                    style={{
                                        background: "var(--color-secondary)",
                                        color: "var(--color-white)",
                                    }}
                                >
                                    {index + 1}/4
                                </div>

                                {playing ? (
                                    <iframe
                                        title={`${stream.label} stream`}
                                        src={frameSrc}
                                        className="w-full h-full block border-0"
                                        allow="autoplay; fullscreen; picture-in-picture"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-center p-6">
                                        <div>
                                            <div
                                                className="text-lg font-extrabold"
                                                style={{ color: "var(--color-white)" }}
                                            >
                                                Ready
                                            </div>
                                            <div className="mt-2 text-xs font-mono break-all text-muted-foreground">
                                                {stream.url}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className="absolute left-0 right-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-black/80 pointer-events-none" />
                            </div>
                        );
                    })}
                    {quadStreams.length === 0 && (
                        <div className="card rounded-2xl p-8 text-muted-foreground">
                            This quad device has no stream URLs configured.
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="flex-1 min-h-0 h-full overflow-y-auto bg-background p-6 flex flex-col gap-6">
            <div
                className={`card rounded-xl p-4 transition-shadow ${playing ? "shadow-2xl" : "shadow-lg"}`}
            >
                {renderHeader()}
            </div>

            {(() => {
                if (!playing && selectedSubDeviceUrl) {
                    return (
                        <div className="flex-1 min-h-0">
                            <div
                                className="w-full rounded-2xl overflow-hidden shadow-2xl"
                                style={{
                                    height: PLAYER_HEIGHT,
                                    background: "#000",
                                }}
                            >
                                <div className="w-full h-full relative">
                                    <video
                                        ref={videoOriginalRef}
                                        muted
                                        autoPlay
                                        playsInline
                                        className="w-full h-full object-cover block"
                                        style={{ background: "#000" }}
                                        poster={thumbSrc || ""}
                                    />
                                </div>
                            </div>
                        </div>
                    );
                }

                if (hasProcessed === false) {
                    return (
                        // single full-width video
                        <div className="flex-1 min-h-0">
                            <div
                                className="w-full rounded-2xl overflow-hidden shadow-2xl"
                                style={{
                                    height: PLAYER_HEIGHT,
                                    background: "var(--color-background)",
                                }}
                            >
                                <div className="w-full h-full relative">
                                    {isLoading && (
                                        <div
                                            className="absolute left-4 top-4 z-30"
                                            style={{
                                                color: "var(--color-lightgrey)",
                                            }}
                                        >
                                            Loading...
                                        </div>
                                    )}
                                    {error && (
                                        <div
                                            className="absolute left-4 top-4 z-30"
                                            style={{
                                                color: "var(--color-alert)",
                                            }}
                                        >
                                            Error: {error}
                                        </div>
                                    )}
                                    <div
                                        className="absolute left-3 top-3 z-30 px-3 py-1 rounded-full backdrop-blur font-bold text-sm"
                                        style={{
                                            background: "rgba(0,0,0,0.4)",
                                            color: "var(--color-white)",
                                        }}
                                    >
                                        Original
                                    </div>
                                    {renderMode.original === "iframe" ? (
                                        <iframe
                                            title="Original stream"
                                            src={originalIframeSrc}
                                            className="w-full h-full block border-0"
                                            allow="autoplay; fullscreen; picture-in-picture"
                                        />
                                    ) : (
                                        <video
                                            ref={videoOriginalRef}
                                            controls
                                            autoPlay
                                            muted
                                            playsInline
                                            className="w-full h-full object-cover block"
                                            style={{
                                                background:
                                                    "var(--color-background)",
                                            }}
                                        />
                                    )}
                                    <div className="absolute left-0 right-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-black/80 pointer-events-none" />
                                </div>
                            </div>
                        </div>
                    );
                }

                // two-column layout (either hasProcessed === true or detection pending)
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 flex-1 min-h-0">
                        {/* Original stream card - modern full-bleed look, title pill inside */}
                        <div
                            className="relative rounded-2xl overflow-hidden flex flex-col shadow-2xl"
                            style={{
                                maxHeight: PLAYER_HEIGHT,
                                background: "var(--color-background)",
                            }}
                        >
                            <div className="flex-1 min-h-0 relative">
                                {isLoading && (
                                    <div
                                        className="absolute left-4 top-4 z-30"
                                        style={{
                                            color: "var(--color-lightgrey)",
                                        }}
                                    >
                                        Loading...
                                    </div>
                                )}
                                {error && (
                                    <div
                                        className="absolute left-4 top-4 z-30"
                                        style={{ color: "var(--color-alert)" }}
                                    >
                                        Error: {error}
                                    </div>
                                )}

                                <div className="w-full h-full relative overflow-hidden">
                                    {/* title pill */}
                                    <div
                                        className="absolute left-3 top-3 z-30 px-3 py-1 rounded-full backdrop-blur font-bold text-sm"
                                        style={{
                                            background: "rgba(0,0,0,0.4)",
                                            color: "var(--color-white)",
                                        }}
                                    >
                                        Original
                                    </div>

                                    {renderMode.original === "iframe" ? (
                                        <iframe
                                            title="Original stream"
                                            src={originalIframeSrc}
                                            className="w-full h-full block border-0"
                                            allow="autoplay; fullscreen; picture-in-picture"
                                        />
                                    ) : (
                                        <video
                                            ref={videoOriginalRef}
                                            controls
                                            autoPlay
                                            muted
                                            playsInline
                                            className="w-full h-full object-cover block"
                                            style={{
                                                background:
                                                    "var(--color-background)",
                                            }}
                                        />
                                    )}

                                    {/* stronger bottom gradient for controls contrast */}
                                    <div className="absolute left-0 right-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-black/80 pointer-events-none" />
                                </div>
                            </div>
                        </div>

                        {/* Processed stream card - distinct accent and pill */}
                        <div
                            className="relative rounded-2xl overflow-hidden flex flex-col shadow-xl"
                            style={{
                                maxHeight: PLAYER_HEIGHT,
                                background: "var(--color-tertiary)",
                            }}
                        >
                            <div className="flex-1 min-h-0 relative">
                                {isLoading && (
                                    <div
                                        className="absolute left-4 top-4 z-30"
                                        style={{
                                            color: "var(--color-lightgrey)",
                                        }}
                                    >
                                        Loading...
                                    </div>
                                )}
                                {error && (
                                    <div
                                        className="absolute left-4 top-4 z-30"
                                        style={{ color: "var(--color-alert)" }}
                                    >
                                        Error: {error}
                                    </div>
                                )}

                                <div className="w-full h-full relative overflow-hidden">
                                    {/* title pill */}
                                    <div
                                        className="absolute left-3 top-3 z-30 px-3 py-1 rounded-full backdrop-blur font-bold text-sm"
                                        style={{
                                            background: "rgba(0,0,0,0.4)",
                                            color: "var(--color-white)",
                                        }}
                                    >
                                        Processed
                                    </div>

                                    {/* small tag on top-right */}
                                    <div
                                        className="absolute right-3 top-3 z-30 px-2.5 py-1 rounded-md font-bold text-xs border"
                                        style={{
                                            background: "var(--color-teal)",
                                            color: "var(--color-white)",
                                            borderColor: "transparent",
                                        }}
                                    >
                                        AI
                                    </div>

                                    {renderMode.processed === "iframe" ? (
                                        <iframe
                                            title="Processed stream"
                                            src={processedIframeSrc}
                                            className="w-full h-full block border-0"
                                            allow="autoplay; fullscreen; picture-in-picture"
                                        />
                                    ) : (
                                        <video
                                            ref={videoProcessedRef}
                                            controls
                                            autoPlay
                                            muted
                                            playsInline
                                            className="w-full h-full object-cover block"
                                            style={{
                                                background:
                                                    "var(--color-background)",
                                            }}
                                        />
                                    )}

                                    <div className="absolute left-0 right-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-black/80 pointer-events-none" />
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};
